import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ControlValueAccessorComponent } from '../../core';
import { LibCoreLayoutModule } from '../core-layout';
import { PipesModule } from '../../pipes';
import { DialogService } from '../../services';
import { BusinessObject, DEBOUNCE_TIME, Product } from '../../models';
import { BusinessObjectUtils } from '../../utils';
import {
  ServiceSelectionDialogComponent,
  ServiceSelectionDialogComponentData,
} from './service-selection-dialog/service-selection-dialog.component';

@Component({
  selector: 'lib-service-selection-form',
  templateUrl: './service-selection-form.component.html',
  styleUrls: ['./service-selection-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    LibCoreLayoutModule,
    PipesModule,
    TranslateModule,
    // material design
    MatButtonModule,
    MatIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ServiceSelectionFormComponent),
      multi: true,
    },
  ],
})
export class ServiceSelectionFormComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  public readonly DURATION_OFFSET = 15;

  private _businessObject: BusinessObject;
  @Input() set businessObject(value: BusinessObject) {
    this._businessObject = value;
    this.updateSelectedProducts();
  }
  get businessObject(): BusinessObject {
    return this._businessObject;
  }

  private _selectedTime: Date;
  @Input() set selectedTime(value: Date) {
    this._selectedTime = value;
    this.updateTotalPrice();
  }
  get selectedTime(): Date {
    return this._selectedTime;
  }

  private _estimatedDuration: number;
  @Input() set estimatedDuration(value: number) {
    this._estimatedDuration = value;

    if (value) {
      this.durationControl.setValue(value);
    }
  }
  get estimatedDuration(): number {
    return this._estimatedDuration;
  }
  @Input() enableCustomDuration: boolean;

  @Output() durationChanged: EventEmitter<number> = new EventEmitter();

  public isDisabled = false;

  public selectedProducts: Product[];
  public totalPrice: number;
  public durationControl = new FormControl<number>(null);

  private selectedProductIdsControl = new FormControl<number[]>([]);

  constructor(private dialogService: DialogService) {
    super();

    this.selectedProductIdsControl.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((value) => {
        this._onChange(value || []);

        this.updateSelectedProducts();

        // only auto calculate duration if estimatedDuration is not set
        const newDuration = BusinessObjectUtils.calculateTotalDuration(
          this.businessObject,
          this.selectedProductIdsControl.value
        );

        this.durationControl.setValue(newDuration || this.estimatedDuration);
      });

    this.durationControl.valueChanges
      .pipe(distinctUntilChanged())
      .pipe(debounceTime(DEBOUNCE_TIME))
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((duration) =>
        this.durationChanged.emit(duration || this.estimatedDuration)
      );
  }

  public isAbleToChangeDurationByOffset(offset: number): boolean {
    const newValue: number = (this.durationControl.value || 0) + offset;

    return !this.isDisabled && this.enableCustomDuration && newValue > 0;
  }

  public changeDurationByOffset(offset: number): void {
    if (!this.isAbleToChangeDurationByOffset(offset)) {
      return;
    }

    const newValue: number = this.durationControl.value + offset;

    this.durationControl.setValue(newValue);
  }

  public isAnyDiscountActivated(product: Product): boolean {
    return BusinessObjectUtils.isAnyDiscountActivatedAtTimeStamp(
      product,
      this.selectedTime
    );
  }

  public getDiscountPrice(product: Product): number {
    return BusinessObjectUtils.getPromotionalPrice(product, this.selectedTime);
  }

  public isAbleToSelectService(): boolean {
    return !this.isDisabled && !!this.businessObject;
  }

  public openServiceSelectionDialog(): void {
    if (!this.isAbleToSelectService()) {
      return;
    }

    const dialogRef = this.dialogService.openDialog(
      ServiceSelectionDialogComponent,
      this.dialogService.createLargeDialogConfig({
        businessObject: this.businessObject,
        selectedProductIds: this.selectedProductIdsControl.value,
        selectedTime: this.selectedTime,
      } as ServiceSelectionDialogComponentData)
    );

    dialogRef
      .afterClosed()
      .pipe(filter((selectedProductIds) => selectedProductIds?.length > 0))
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((selectedProductIds) => {
        this.selectedProductIdsControl.setValue(selectedProductIds);
      });
  }

  public removeProduct(productId: number): void {
    if (this.isDisabled) {
      return;
    }

    const selectedProductIds = this.selectedProductIdsControl.value?.filter(
      (id) => id !== productId
    );
    this.selectedProductIdsControl.setValue(selectedProductIds);
  }

  private updateSelectedProducts(): void {
    this.selectedProducts = BusinessObjectUtils.getListOfProducts(
      this.businessObject,
      this.selectedProductIdsControl.value
    );

    this.updateTotalPrice();
  }

  private updateTotalPrice(): void {
    this.totalPrice = 0;
    this.selectedProducts?.forEach((p) => {
      if (
        BusinessObjectUtils.isAnyDiscountActivatedAtTimeStamp(
          p,
          this.selectedTime
        )
      ) {
        this.totalPrice += BusinessObjectUtils.getPromotionalPrice(
          p,
          this.selectedTime
        );
      } else {
        this.totalPrice += p.price;
      }
    });
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(productIds: number[]) {
    this.selectedProductIdsControl.setValue([...(productIds || [])]);
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }
}
